/**
 * Cross-cutting types and primitive constants: `Feature`/`Structure` shapes
 * and deployment config, plus the `TLabel` translation primitives re-exported
 * from `@repo/shared/translations/label`.
 *
 * Where the platform's vocabulary is defined. `Feature` and `Structure` are
 * the extension contract: a structure declares config slots via
 * `receivesConfigDefaults`, a feature fills them via `providesConfig`, and
 * `assembleConfig` merges them at render.
 */

import { EMPTY_OBJECT, type FormatParams, formatString, labelToKey, type TLabel, t } from '@repo/shared/translations/label';
import { type ImageResolvedAssetSource } from 'react-native';

export { EMPTY_OBJECT, type FormatParams, labelToKey, type TLabel, t };

export type TLabelOrText = {
    label?: TLabel;
    text?: string;
};

export type makeTestIdArgs = {
    testID?: string | null;
    ariaLabel?: TLabel | null;
    label?: TLabel | null;
    text?: string | null;
    formatParams?: FormatParams | null;
};
export function makeTestID({ testID, ariaLabel, label, text, formatParams }: makeTestIdArgs): string | undefined {
    if (testID) {
        return testID;
    } else if (ariaLabel?.defaultText) {
        return formatString(ariaLabel.defaultText, formatParams ?? {});
    } else if (label?.defaultText) {
        return formatString(label.defaultText, formatParams ?? {});
    } else if (text) {
        return text;
    }
    return undefined;
}

// A feature provides values for config slots exposed by a structure or parent
// feature. It may also expose new config slots that sub features can provide
// values for. Most work is done in features.
export type Feature<TProvides = any, TReceives = any> = {
    name: string;
    label?: TLabel;
    key: string;
    dev?: boolean;
    showToggle?: boolean;
    both?: boolean;
    providesConfig?: Partial<TProvides>;
    receivesConfigDefaults?: TReceives;
    subscreens?: Record<string, React.ComponentType<any>>;
    missingConfigAllowed?: string[];
    parentFeature?: string;
    capabilities?: string[];
};

// A structure defines a type of instance (e.g. question) and provdes config
// slots that features can use to provide functionality.
export type Structure<T> = {
    key: string;
    name: string;
    screen?: React.ComponentType<any>;
    receivesConfigDefaults?: T;
    subscreens?: Record<string, React.ComponentType<any>>;
    teaser?: React.ComponentType<any>;
    overlayScreen?: React.ComponentType<any>;
    capabilities?: string[];
    isAdminUi?: boolean;
};

export type FirebaseConfig = {
    apiKey: string;
    authDomain: string;
    projectId: string;
    messagingSenderId: string;
    appId: string;
};

export type SiloDefaultFeatures = {
    [structureKey: string]: Record<string, boolean>;
};

export type DeploymentConfig = {
    /** The base URL of the API server (default: `/api`) */
    apiBaseUrl?: string;
    baseUrl?: string;
    basePath?: string;
    firebaseConfig?: FirebaseConfig;
    emulateFirebase?: boolean;
    defaultToAdminDash?: boolean;
    defaultFeatures?: Record<string, SiloDefaultFeatures>;
    ssoDebug?: boolean;
    siloConfig?: Record<string, SiloConfig>;
    defaultLanguage?: string;
    allowedSilos?: string[]; // If set, only these silos are allowed to be used
    skipLoginProviderScreen?: boolean;
    errorMonitoringDsn?: string;
    psiBrandName?: string; // Replaces the name "PSI" with a partner's preferred brand name such as "ZDFspaces"
    faqUrl?: string; // URL of FAQ page
    enableDevLogin?: boolean; // Enable dev login for local development
};

/**
 * Login-screen overrides for a partner-branded silo, layered over the login
 * structure's own config and read synchronously, because the login screen
 * renders before authentication.
 *
 * Labels are plain strings rather than `TLabel`s: a silo that brands its login
 * screen lists a single locale in `siloLanguagesConfig`, so the strings are
 * written in that language instead of being translated.
 */
export type LoginBranding = {
    loginHeadingLabel?: string;
    loginSubheadingLabel?: string;
    showCookieNotice?: boolean;
    brandLogoImages?: string[];
    loginPreviewImage?: string;
};

export type SiloConfig = {
    themeKey?: string;
    defaultLanguage?: string;
    parentSilo?: string;
    hideCookieNoticeBanner?: boolean;
    loginBranding?: LoginBranding;
    multiLanguage?: boolean;
    /**
     * If set, language flags rendered on this silo are prefixed with a country
     * flag. Currently only `'CH'` (the Swiss cross) is supported. Used to
     * convey national cohesion across Swiss linguistic variants.
     */
    languageFlagCountryPrefix?: 'CH';
    textVariants?: {
        aboutBanner?: string;
    };
    titledWriterBadgeSource?: string | ImageResolvedAssetSource;
    titledWriterPredefinedOptions?: string[];
    standalonePageConfig?: {
        header?: {
            logo?: {
                /** Desktop logo image */
                desktop: string | ReturnType<NodeJS.Require>;
                /** Mobile logo (optional, falls back to desktop) */
                mobile: string | ReturnType<NodeJS.Require>;
            };
            /**
             * If set, makes the logo in the header clickable and links to this
             * URL
             */
            logoLinkUrl?: string;
            /**
             * Alt text for the logo, used for accessibility and as fallback
             * text
             */
            alt?: string;
            /**
             * Per-language logos for multi-language silos, keyed by language
             * prefix (e.g. 'fr'). Takes precedence over `logo` when an entry
             * matches the resolved language.
             */
            logoByLanguage?: Record<
                string,
                {
                    /** Logo image for this language */
                    logo: string | ReturnType<NodeJS.Require>;
                    /** Alt text for this language's logo */
                    alt?: string;
                }
            >;
            /**
             * Forces the `logoByLanguage` language per hostname, for silos
             * served from one domain per language. Hostnames not listed fall
             * back to the current UI language.
             */
            logoLanguageByHostname?: Record<string, string>;
        };
        /**
         * footer config, both properties need to be set for the footer to show.
         */
        footer?: {
            /**
             * The text used for the link to linkUrl in the footer on the left
             * of the footer
             */
            linkText: string;
            /**
             * The URL used for the link in the footer on the left of the footer
             */
            linkUrl: string;
            /**
             * Override label for the privacy notice legal link (defaults to
             * "Privacy Notice")
             */
            privacyNoticeLabel?: string;
            /**
             * Override label for the terms of service legal link (defaults to
             * "Terms of Service")
             */
            termsOfServiceLabel?: string;
        };
    };
};

export type SortOrder = 'asc' | 'desc';

export type NotificationToast = {
    id: string;
    type: 'info' | 'success' | 'error';
    label: TLabel;
    dismissable?: boolean;
    formatParams?: FormatParams;
};

export type Direction = 'up' | 'down';
